import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { styled, IconButton, Tooltip, AppText, Row, Column, Flex, AppIcon, useTheme, Sensor, } from '@streem/ui-react';
import { useRoomSession } from '../../../../context/room_session';
import { useObservable } from '../../../../hooks/rxjs';
import { recordElementClicked, recordTooltipViewed } from '@streem/analytics';
const networkLevelMessageMap = {
    2: 'Customer Has Below Average Connection',
    1: 'Customer Has a Bad Connection',
    0: 'Customer Lost Connection',
};
export const NetworkQualityDropdown = ({ showNetworkQuality }) => {
    const theme = useTheme();
    const roomSession = useRoomSession();
    const [remoteNetworkQuality] = useObservable(roomSession.media.remoteNetworkQuality);
    const [isBadRemoteNetworkQuality, setIsBadRemoteNetworkQuality] = useState(false);
    const [enableDropdown, setEnableDropdown] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [networkLevelMessage, setNetworkLevelMessage] = useState('');
    const dropdownEllipsisRef = useRef(null);
    const dropdownTooltipId = 'network-quality-dropdown-tooltip';
    const iconButtonId = 'network-quality-dropdown-button';
    useEffect(() => {
        if (!showNetworkQuality ||
            remoteNetworkQuality === null ||
            remoteNetworkQuality === undefined)
            return;
        if (remoteNetworkQuality < 3) {
            setEnableDropdown(true);
            setIsBadRemoteNetworkQuality(remoteNetworkQuality < 2);
            setNetworkLevelMessage(networkLevelMessageMap[remoteNetworkQuality] ?? '');
        }
        else {
            setEnableDropdown(false);
            // handle situation where dropdown was previously opened
            setShowDropdown(false);
        }
    }, [remoteNetworkQuality, showNetworkQuality]);
    const handleDropdownClick = useCallback(() => {
        setShowDropdown(!showDropdown);
        if (showDropdown) {
            recordElementClicked(iconButtonId);
        }
        setShowTooltip(false);
    }, [showDropdown]);
    const handleTooltipMouseEnter = useCallback(() => {
        setShowTooltip(!showDropdown);
        recordTooltipViewed(dropdownTooltipId);
    }, [showDropdown]);
    return (_jsxs(_Fragment, { children: [_jsx(NetworkQualityDropdownTooltipContainer, { children: enableDropdown ? (_jsx(Tooltip, { message: _jsx(AppText, { color: theme.colors.black, size: "small", children: "More Information" }), showTooltip: showTooltip, placement: "bottom", fallbackPlacements: ['bottom'], dataTestId: dropdownTooltipId, children: _jsx("div", { ref: dropdownEllipsisRef, children: _jsx(IconButton, { iconName: "VerticalEllipsesIcon", label: "Network Quality Dropdown", "data-testid": iconButtonId, onClick: handleDropdownClick, fill: theme.colors.azure, hoverFill: theme.colors.azure, onMouseEnter: handleTooltipMouseEnter, onMouseLeave: () => setShowTooltip(false) }) }) })) : (_jsx(IconButton, { iconName: "VerticalEllipsesIcon", label: "Network Quality Dropdown", "data-testid": iconButtonId, fill: theme.colors.grey30, style: { cursor: 'not-allowed' } })) }), enableDropdown && showDropdown && (_jsx(Sensor, { onClickOutside: event => {
                    if (event?.target instanceof Element &&
                        dropdownEllipsisRef?.current?.contains(event.target))
                        return;
                    setShowDropdown(false);
                }, onEscape: () => setShowDropdown(false), children: _jsx(Tooltip, { message: _jsxs(DropdownContainer, { children: [_jsx(ErrorTextContainer, { isBadRemoteNetworkQuality: isBadRemoteNetworkQuality, children: _jsx(AppText, { style: {
                                        color: isBadRemoteNetworkQuality
                                            ? theme.colors.red60
                                            : theme.colors.orange60,
                                    }, children: networkLevelMessage }) }), _jsx(Row, { style: {
                                    marginTop: '24px',
                                    marginBottom: '24px',
                                    paddingLeft: '16px',
                                }, children: _jsx(AppText, { headingFontFamily: true, as: "h5", style: { fontSize: '18px' }, children: "Recommendation for Customer" }) }), _jsx(Recommendation, { text: "Try switching network types (Wi-Fi/Cellular)", iconName: "WifiRouterIcon" }), _jsx(Recommendation, { text: "Close other apps on their device", iconName: "TouchPhoneIcon" })] }), showTooltip: showDropdown, arrowSize: "0px", distance: 20, offsetValue: -196, placement: "bottom", fallbackPlacements: ['bottom'], dataTestId: "network-quality-dropdown", padding: "0px" }) }))] }));
};
const Recommendation = ({ text, iconName }) => {
    return (_jsxs(Row, { marginRight: '12px', marginBottom: '16px', children: [_jsx(Row, { width: "50px", justifyContent: 'center', children: _jsx(AppIcon, { name: iconName }) }), _jsx(AppText, { children: text })] }));
};
const NetworkQualityDropdownTooltipContainer = styled(Flex)(() => ({
    marginInlineStart: 'auto',
}));
const DropdownContainer = styled(Column)();
const ErrorTextContainer = styled(Row)(({ theme, isBadRemoteNetworkQuality }) => ({
    justifyContent: 'center',
    alignItems: 'center',
    background: isBadRemoteNetworkQuality ? theme.colors.errorPink : theme.colors.orange05,
    height: '45px',
}));
//# sourceMappingURL=network_quality_dropdown_menu.js.map